import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ethers } from 'ethers';

/* ─── ABIs ─────────────────────────────────────────────────────────── */
const PLATFORM_ABI = [
  "function setTreasury(address) external",
  "function setExchangeFee(uint256) external",
  "function setLinkManager(address) external",
  "function pausePlatform() external",
  "function unpausePlatform() external",
  "function approveVendor(address) external",
  "function rejectVendor(address) external",
  "function deactivateVendor(address) external",
  "function reactivateVendor(address) external",
  "function setVendorMintCap(address, uint256) external",
  "function adminSetUserPointCap(address, uint256) external",
  "function rescueStranded(address, address, address) external",
  "function applyAsVendor(string, uint256, uint256) external",
  "function addUserPoints(address, uint256) external",
  "function batchAddUserPoints(address[], uint256[]) external",
  "function setUserPointCap(uint256) external",
  "function proposeRateChange(uint256) external",
  "function applyRateChange() external",
  "function cancelRateChange() external",
  "function setTierRates(uint256, uint256, uint256) external",
  "function setUserTier(address, uint8) external",
  "function startPromotion(uint256, uint256) external",
  "function endPromotion() external",
  "function startWindDown(uint256) external",
  "function cancelWindDown() external",
  "function selfPause() external",
  "function selfResume() external",
  "function convertToROY(address, uint256, uint256) external",
  "function exchangePoints(address, address, uint256, uint256) external",
  "function transferPoints(address, address, uint256) external",
  "function claimLeftoverROY() external",
  "function checkPointStatus(address) external",
  "function burn(uint256) external",
  "function getPlatformAnalytics() external view returns (uint256, uint256, uint256, uint256, uint256)",
  "function getVendorCount() external view returns (uint256)",
  "function isRateChangeReady(address) external view returns (bool)",
  "function rateTimelockRemaining(address) external view returns (uint256)",
  "function effectiveRate(address, address) external view returns (uint256)",
  "function getPointBalance(address, address) external view returns (uint256, uint256, uint8)",
  "function vendorList(uint256) external view returns (address)",
  "function leftoverROY(address) external view returns (uint256)",
  "function exchangeFeeBps() external view returns (uint256)",
  "function treasury() external view returns (address)",
  "function paused() external view returns (bool)",
  "function balanceOf(address) external view returns (uint256)",
  "function totalSupply() external view returns (uint256)",
  "function ADMIN_ROLE() external view returns (bytes32)",
  "function VENDOR_ROLE() external view returns (bytes32)",
  "function hasRole(bytes32, address) external view returns (bool)",
];
const VIEWS_ABI = [
  "function getVendorPage(uint256, uint256) external view returns (address[], string[], uint256[])",
  "function getBalancePage(address, uint256, uint256) external view returns (uint256[], uint256[], uint256[])",
  "function getExpiryPage(address, uint256, uint256) external view returns (bool[], bool[], uint256[])",
  "function getPortfolioValue(address) external view returns (uint256)",
  "function simulateConvert(address, address, uint256) external view returns (uint256, uint256)",
  "function simulateExchange(address, address, uint256) external view returns (uint256, uint256, uint256)",
];
const LM_ABI = [
  "function configureLinking(address, uint256, uint256) external",
  "function toggleLinking(bool, bool) external",
  "function revokeLink(address) external",
  "function linkExternalAccount(address, string, uint256, uint256, uint256, bytes) external",
  "function importPoints(address, uint256) external",
  "function unlinkAccount(address) external",
  "function isLinked(address, address) external view returns (bool)",
  "function getLinkStatus(address, address) external view returns (string, uint256, uint256, uint256, uint256, bool)",
  "function getLinkConfig(address) external view returns (address, uint256, uint256, bool, bool)",
  "function simulateImport(address, uint256) external view returns (uint256, uint256)",
];

/* ─── Design System ─────────────────────────────────────────────────── */
const C = {
  // Primary green palette — matches website #85BB65
  green:      '#85BB65',
  greenDark:  '#5E9444',
  greenLight: '#A8D48A',
  greenGlow:  'rgba(133,187,101,0.12)',
  greenBorder:'rgba(133,187,101,0.28)',
  // Backgrounds - light silver theme matching website
  bg:         '#F0F2F0',
  bg2:        '#E8EBE8',
  surface:    '#FAFBFA',
  surface2:   '#ECEEED',
  glass:      'rgba(250,251,250,0.92)',
  // Borders
  border:     '#D8DDD8',
  borderHover:'#C0C8C0',
  // Text
  text:       '#1A1E1A',
  text2:      '#4A5248',
  text3:      '#8A948A',
  // Accents
  amber:      '#c17f24',
  amberMuted: 'rgba(193,127,36,0.08)',
  amberBorder:'rgba(193,127,36,0.22)',
  blue:       '#3a7abf',
  blueMuted:  'rgba(58,122,191,0.08)',
  blueBorder: 'rgba(58,122,191,0.22)',
  red:        '#d94f4f',
  redMuted:   'rgba(217,79,79,0.08)',
  redBorder:  'rgba(217,79,79,0.22)',
  // Success/error
  success:    '#3aaa5e',
  error:      '#d94f4f',
};

const fadeUp = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { show: { transition: { staggerChildren: 0.06 } } };

/* ─── Global styles injected once ─────────────────────────────────── */
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Raleway:wght@400;500;600;700;800&family=Open+Sans:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
  * { box-sizing: border-box; }
  body { margin: 0; background: #F0F2F0; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: #E8EBE8; }
  ::-webkit-scrollbar-thumb { background: rgba(133,187,101,0.4); border-radius: 2px; }
  input::placeholder, textarea::placeholder { color: #B0BCB0; }
  select option { background: #FAFBFA; color: #1A1E1A; }
  @media (max-width: 640px) {
    .grid-2 { grid-template-columns: 1fr !important; }
    .grid-4 { grid-template-columns: 1fr 1fr !important; }
    .hide-mobile { display: none !important; }
    .tab-label { display: none !important; }
  }
`;

/* ─── Primitives ─────────────────────────────────────────────────── */

function Field({ label, value, onChange, placeholder, type = 'text', mono }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: 12 }}>
      {label && <label style={{ display: 'block', fontFamily: "'Raleway', sans-serif", fontWeight: 600, fontSize: 10, color: C.text3, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 5 }}>{label}</label>}
      <input
        type={type} value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          width: '100%', background: focused ? C.surface2 : C.surface,
          border: `1px solid ${focused ? C.greenBorder : C.border}`,
          color: C.text, padding: '10px 13px',
          fontFamily: mono ? "'JetBrains Mono', monospace" : "'Open Sans', sans-serif",
          fontSize: 13, borderRadius: 9, outline: 'none',
          transition: 'all 0.15s',
          boxShadow: focused ? `0 0 0 3px ${C.greenGlow}` : 'none',
        }}
      />
    </div>
  );
}

function TextArea({ label, value, onChange, placeholder, rows = 4 }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: 12 }}>
      {label && <label style={{ display: 'block', fontFamily: "'Raleway', sans-serif", fontWeight: 600, fontSize: 10, color: C.text3, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 5 }}>{label}</label>}
      <textarea
        value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} rows={rows}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{
          width: '100%', background: focused ? C.surface2 : C.surface,
          border: `1px solid ${focused ? C.greenBorder : C.border}`,
          color: C.text, padding: '10px 13px',
          fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
          borderRadius: 9, outline: 'none', resize: 'vertical',
          transition: 'all 0.15s',
          boxShadow: focused ? `0 0 0 3px ${C.greenGlow}` : 'none',
        }}
      />
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <div style={{ marginBottom: 12 }}>
      {label && <label style={{ display: 'block', fontFamily: "'Raleway', sans-serif", fontWeight: 600, fontSize: 10, color: C.text3, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 5 }}>{label}</label>}
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, color: C.text, padding: '10px 13px', fontFamily: "'Open Sans', sans-serif", fontSize: 13, borderRadius: 9, outline: 'none', cursor: 'pointer' }}>
        {options.map((o, i) => <option key={i} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function Btn({ label, onClick, loading, variant = 'primary', small, full, icon }) {
  const [hovered, setHovered] = useState(false);
  const styles = {
    primary: { bg: C.green, bgH: C.greenDark, color: '#0A0F0A', border: 'none', shadow: `0 0 20px ${C.greenGlow}` },
    ghost:   { bg: 'transparent', bgH: C.surface2, color: C.text2, border: `1px solid ${C.border}`, shadow: 'none' },
    green:   { bg: C.greenGlow, bgH: 'rgba(34,197,94,0.2)', color: C.green, border: `1px solid ${C.greenBorder}`, shadow: 'none' },
    red:     { bg: C.redMuted, bgH: 'rgba(248,113,113,0.16)', color: C.red, border: `1px solid ${C.redBorder}`, shadow: 'none' },
    amber:   { bg: C.amberMuted, bgH: 'rgba(245,158,11,0.18)', color: C.amber, border: `1px solid ${C.amberBorder}`, shadow: 'none' },
    blue:    { bg: C.blueMuted, bgH: 'rgba(96,165,250,0.16)', color: C.blue, border: `1px solid ${C.blueBorder}`, shadow: 'none' },
  };
  const s = styles[variant] || styles.primary;
  return (
    <button onClick={onClick} disabled={loading}
      onMouseOver={() => setHovered(true)} onMouseOut={() => setHovered(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        background: hovered && !loading ? s.bgH : s.bg,
        color: s.color, border: s.border,
        boxShadow: hovered && variant === 'primary' ? s.shadow : 'none',
        fontFamily: "'Raleway', sans-serif", fontWeight: 700,
        fontSize: small ? 11 : 12, letterSpacing: '0.04em',
        padding: small ? '6px 12px' : '9px 16px',
        borderRadius: 8, cursor: loading ? 'not-allowed' : 'pointer',
        opacity: loading ? 0.5 : 1, transition: 'all 0.15s',
        width: full ? '100%' : 'auto', whiteSpace: 'nowrap',
      }}>
      {icon && <span>{icon}</span>}
      {loading ? '···' : label}
    </button>
  );
}

function Card({ title, badge, children, glow, flat }) {
  return (
    <div style={{
      background: flat ? 'transparent' : C.surface,
      border: `1px solid ${glow ? C.greenBorder : C.border}`,
      borderRadius: 14,
      padding: flat ? 0 : '18px 18px 16px',
      boxShadow: glow ? `0 0 32px ${C.greenGlow}` : 'none',
      transition: 'border-color 0.2s',
    }}>
      {title && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <span style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 700, fontSize: 13, color: C.text, letterSpacing: '0.02em' }}>{title}</span>
          {badge && <span style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: C.greenGlow, color: C.green, border: `1px solid ${C.greenBorder}` }}>{badge}</span>}
        </div>
      )}
      {children}
    </div>
  );
}

function Alert({ children, type = 'info' }) {
  const colors = { info: [C.green, C.greenGlow, C.greenBorder], warn: [C.amber, C.amberMuted, C.amberBorder], error: [C.red, C.redMuted, C.redBorder] };
  const [c, bg, border] = colors[type] || colors.info;
  return (
    <div style={{ padding: '10px 13px', borderRadius: 9, background: bg, border: `1px solid ${border}`, marginBottom: 12, fontFamily: "'Open Sans', sans-serif", fontSize: 12, color: c, lineHeight: 1.6 }}>
      {children}
    </div>
  );
}

function KV({ label, value, highlight, mono }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
      <span style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 12, color: C.text3 }}>{label}</span>
      <span style={{ fontFamily: mono ? "'JetBrains Mono', monospace" : "'Raleway', sans-serif", fontWeight: 600, fontSize: 12, color: highlight ? C.green : C.text }}>{value}</span>
    </div>
  );
}

function Divider({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0' }}>
      <div style={{ flex: 1, height: 1, background: C.border }} />
      {label && <span style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 10, color: C.text3, letterSpacing: '0.08em' }}>{label}</span>}
      <div style={{ flex: 1, height: 1, background: C.border }} />
    </div>
  );
}

/* ─── Main App ───────────────────────────────────────────────────── */
export default function Dapp() {
  const [step, setStep]           = useState('setup');
  const [addrs, setAddrs]         = useState({ platform: '', views: '', lm: '' });
  const [account, setAccount]     = useState('');
  const [contracts, setContracts] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading]     = useState(false);
  const [toast, setToast]         = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = GLOBAL_CSS;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  const connect = async () => {
    if (!window.ethereum) { showToast('MetaMask not found', false); return; }
    try {
      await window.ethereum.request({ method: 'eth_requestAccounts' });
      const provider = new ethers.BrowserProvider(window.ethereum);
      const net = await provider.getNetwork();
      if (net.chainId !== 31337n) {
        try { await window.ethereum.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: '0x7A69' }] }); }
        catch { showToast('Switch to Hardhat Local (31337)', false); return; }
      }
      const signer   = await provider.getSigner();
      const addr     = await signer.getAddress();
      const pAddr    = addrs.platform || '0x5FbDB2315678afecb367f032d93F642f64180aa3';
      const vAddr    = addrs.views    || '0xe7f1725E7734CE288F8367e1Bb143E90bb3F0512';
      const lAddr    = addrs.lm       || '0x9fE46736679d2D9a65F0992F2272dE9f3c7fa6e0';
      const platform = new ethers.Contract(pAddr, PLATFORM_ABI, signer);
      const views    = new ethers.Contract(vAddr, VIEWS_ABI, signer);
      const lm       = new ethers.Contract(lAddr, LM_ABI, signer);
      setAccount(addr);
      setContracts({ platform, views, lm, signer, provider });
      setStep('connected');
      showToast('Wallet connected ✓');
    } catch (e) { showToast(e.message.slice(0, 80), false); }
  };

  const loadDashboard = async () => {
    if (!contracts) return;
    setLoading(true);
    try {
      const { platform, views } = contracts;
      const [roy, count, fee, paused, portfolio, leftover] = await Promise.all([
        platform.balanceOf(account),
        platform.getVendorCount(),
        platform.exchangeFeeBps(),
        platform.paused(),
        views.getPortfolioValue(account),
        platform.leftoverROY(account),
      ]);
      const ADMIN_ROLE  = await platform.ADMIN_ROLE();
      const VENDOR_ROLE = await platform.VENDOR_ROLE();
      const [isAdmin, isVendor] = await Promise.all([
        platform.hasRole(ADMIN_ROLE, account),
        platform.hasRole(VENDOR_ROLE, account),
      ]);
      let balances = [], expiryWarnings = [];
      if (count > 0n) {
        const vp = await views.getVendorPage(0, Number(count));
        const ep = await views.getExpiryPage(account, 0, Number(count));
        for (let i = 0; i < vp[0].length; i++) {
          const pb = await platform.getPointBalance(account, vp[0][i]);
          balances.push({ addr: vp[0][i], name: vp[1][i], points: pb[0].toString(), expiry: pb[1].toString(), tier: Number(pb[2]) });
          if (ep[0][i]) expiryWarnings.push(vp[1][i]);
        }
      }
      setDashboard({
        roy: ethers.formatEther(roy),
        leftover: ethers.formatEther(leftover),
        count: count.toString(), fee: fee.toString(), paused,
        portfolio: ethers.formatEther(portfolio),
        role: isAdmin ? 'Admin' : isVendor ? 'Vendor' : 'User',
        balances, expiryWarnings,
      });
    } catch (e) { showToast('Load error: ' + e.message.slice(0, 60), false); }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (step === 'connected') loadDashboard(); }, [step]);

  const send = async (label, fn) => {
    setLoading(true);
    try {
      const tx = await fn();
      await tx.wait();
      showToast(`${label} confirmed ✓`);
      await loadDashboard();
    } catch (e) {
      showToast((e.reason || e.message || 'Error').slice(0, 80), false);
    }
    setLoading(false);
  };

  /* ── Setup / Connect Screen ── */
  if (step === 'setup') return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px' }}>

      {/* Background glow */}
      <div style={{ position: 'fixed', top: '30%', left: '50%', transform: 'translate(-50%,-50%)', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(133,187,101,0.08), transparent 70%)', pointerEvents: 'none' }} />

      <motion.div initial="hidden" animate="show" variants={fadeUp} style={{ width: '100%', maxWidth: 400 }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 56, height: 56, borderRadius: 16, background: 'linear-gradient(135deg, #85BB65, #5E9444)', boxShadow: `0 8px 32px ${C.greenGlow}`, marginBottom: 16 }}>
            <span style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 800, fontSize: 22, color: '#fff' }}>K</span>
          </div>
          <div style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 800, fontSize: 26, color: C.text, letterSpacing: '-0.01em' }}>Korunto</div>
          <div style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 13, color: C.text3, marginTop: 4 }}>Connect your wallet to access the dashboard</div>
        </div>

        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 18, padding: '24px 22px', boxShadow: '0 8px 40px rgba(0,0,0,0.3)' }}>
          <div style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 600, fontSize: 11, color: C.text3, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 16 }}>Contract Addresses</div>
          {[
            { key: 'platform', label: 'KoruntoPlatform',    placeholder: '0x5FbDB231...' },
            { key: 'views',    label: 'KoruntoViews',       placeholder: '0xe7f1725E...' },
            { key: 'lm',       label: 'KoruntoLinkManager', placeholder: '0x9fE46736...' },
          ].map(f => (
            <Field key={f.key} label={f.label} value={addrs[f.key]} placeholder={f.placeholder} mono
              onChange={v => setAddrs({ ...addrs, [f.key]: v })} />
          ))}

          <button onClick={connect} style={{
            width: '100%', padding: '13px', marginTop: 8,
            background: 'linear-gradient(135deg, #85BB65, #5E9444)',
            color: '#0A0F0A', border: 'none', borderRadius: 10,
            fontFamily: "'Raleway', sans-serif", fontWeight: 800,
            fontSize: 14, letterSpacing: '0.04em', cursor: 'pointer',
            boxShadow: `0 4px 24px ${C.greenGlow}`,
            transition: 'transform 0.15s, box-shadow 0.15s',
          }}
            onMouseOver={e => { e.target.style.transform = 'translateY(-1px)'; e.target.style.boxShadow = `0 8px 32px ${C.greenGlow}`; }}
            onMouseOut={e => { e.target.style.transform = 'none'; e.target.style.boxShadow = `0 4px 24px ${C.greenGlow}`; }}>
            Connect MetaMask →
          </button>

          <div style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 11, color: C.text3, textAlign: 'center', marginTop: 14, lineHeight: 1.6 }}>
            Requires MetaMask · Hardhat Local · Chain 31337
          </div>
        </div>
      </motion.div>
    </div>
  );

  /* ── Connected App Shell ── */
  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '⬡' },
    { id: 'vendor',    label: 'Vendor',    icon: '◈' },
    { id: 'user',      label: 'User',      icon: '◉' },
    { id: 'links',     label: 'Links',     icon: '⊕' },
    { id: 'admin',     label: 'Admin',     icon: '⚙' },
  ];

  const roleColors = { Admin: C.amber, Vendor: C.blue, User: C.green };
  const roleColor = dashboard ? roleColors[dashboard.role] || C.green : C.green;

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text }}>

      {/* BG glow */}
      <div style={{ position: 'fixed', top: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 600, height: 300, background: 'radial-gradient(ellipse at top, rgba(133,187,101,0.06), transparent 70%)', pointerEvents: 'none', zIndex: 0 }} />

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div key="toast" initial={{ opacity: 0, y: 20, x: '-50%' }} animate={{ opacity: 1, y: 0, x: '-50%' }} exit={{ opacity: 0, y: 10, x: '-50%' }}
            style={{
              position: 'fixed', bottom: 24, left: '50%', zIndex: 100,
              padding: '11px 20px', borderRadius: 30,
              background: toast.ok ? 'rgba(34,197,94,0.15)' : 'rgba(248,113,113,0.12)',
              border: `1px solid ${toast.ok ? C.greenBorder : C.redBorder}`,
              fontFamily: "'Open Sans', sans-serif", fontSize: 13,
              color: toast.ok ? C.green : C.red,
              whiteSpace: 'nowrap', boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
              backdropFilter: 'blur(12px)',
            }}>
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top Nav Bar */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 40, height: 56,
        background: 'rgba(250,251,250,0.96)', backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${C.border}`,
        boxShadow: '0 1px 8px rgba(0,0,0,0.05)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px',
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg, #85BB65, #5E9444)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 800, fontSize: 12, color: '#fff' }}>K</span>
          </div>
          <span style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 800, fontSize: 16, color: C.text }}>Korunto</span>
        </div>

        {/* Desktop Tabs */}
        <div className="hide-mobile" style={{ display: 'flex', gap: 2 }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontFamily: "'Raleway', sans-serif", fontWeight: activeTab === t.id ? 700 : 500,
              fontSize: 13, padding: '6px 14px', borderRadius: 8, border: 'none',
              background: activeTab === t.id ? C.greenGlow : 'transparent',
              color: activeTab === t.id ? C.green : C.text3,
              cursor: 'pointer', transition: 'all 0.15s',
            }}>
              <span style={{ fontSize: 11 }}>{t.icon}</span>
              {t.label}
            </button>
          ))}
        </div>

        {/* Account + Mobile menu button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {dashboard && (
            <span style={{ padding: '3px 10px', borderRadius: 20, fontFamily: "'Raleway', sans-serif", fontWeight: 700, fontSize: 10, letterSpacing: '0.06em', background: `${roleColor}18`, color: roleColor, border: `1px solid ${roleColor}30` }} className="hide-mobile">
              {dashboard.role.toUpperCase()}
            </span>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 20, background: C.surface, border: `1px solid ${C.border}` }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.green, boxShadow: `0 0 6px ${C.green}` }} />
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: C.text3 }}>
              {account.slice(0, 6)}···{account.slice(-4)}
            </span>
          </div>
          {/* Mobile hamburger */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 8px', cursor: 'pointer', display: 'none' }}
            className="show-mobile">
            <div style={{ width: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {[0,1,2].map(i => <span key={i} style={{ display: 'block', height: 1.5, background: C.text3, borderRadius: 1 }} />)}
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Bottom Tab Bar */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 40,
        background: 'rgba(250,251,250,0.97)', backdropFilter: 'blur(20px)',
        borderTop: `1px solid ${C.border}`,
        display: 'flex', padding: '8px 0 calc(8px + env(safe-area-inset-bottom))',
        boxShadow: '0 -1px 8px rgba(0,0,0,0.06)',
      }} className="mobile-tabs">
        <style>{`.mobile-tabs { display: none !important; } @media (max-width: 640px) { .mobile-tabs { display: flex !important; } .hide-mobile { display: none !important; } }`}</style>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
              background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0',
              color: activeTab === t.id ? C.green : C.text3,
              transition: 'color 0.15s',
            }}>
            <span style={{ fontSize: 18 }}>{t.icon}</span>
            <span style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 600, fontSize: 9, letterSpacing: '0.06em' }}>{t.label.toUpperCase()}</span>
            {activeTab === t.id && <span style={{ width: 16, height: 2, borderRadius: 1, background: C.green }} />}
          </button>
        ))}
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '72px 16px 80px', position: 'relative', zIndex: 1 }}>
        <AnimatePresence mode="wait">
          <motion.div key={activeTab} initial="hidden" animate="show" exit={{ opacity: 0 }} variants={fadeUp}>
            {activeTab === 'dashboard' && <DashboardTab dashboard={dashboard} loading={loading} loadDashboard={loadDashboard} send={send} contracts={contracts} account={account} />}
            {activeTab === 'vendor'    && <VendorTab    contracts={contracts} account={account} send={send} loading={loading} showToast={showToast} />}
            {activeTab === 'user'      && <UserTab      contracts={contracts} account={account} send={send} loading={loading} showToast={showToast} />}
            {activeTab === 'links'     && <LinksTab     contracts={contracts} account={account} send={send} loading={loading} showToast={showToast} />}
            {activeTab === 'admin'     && <AdminTab     contracts={contracts} account={account} send={send} loading={loading} showToast={showToast} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ─── Page Header ─────────────────────────────────────────────────── */
function PageHeader({ title, subtitle, action }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
      <div>
        <h1 style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 800, fontSize: 28, color: C.text, margin: 0, letterSpacing: '-0.02em' }}>{title}</h1>
        {subtitle && <p style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 13, color: C.text3, margin: '4px 0 0' }}>{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

/* ─── Dashboard ───────────────────────────────────────────────────── */
function DashboardTab({ dashboard, loading, loadDashboard, send, contracts, account }) {
  return (
    <motion.div variants={stagger}>

      <PageHeader title="Dashboard" subtitle="Your loyalty overview"
        action={<Btn label={loading ? '···' : '↻ Refresh'} variant="ghost" small onClick={loadDashboard} />} />

      {dashboard ? (
        <>
          {/* Alerts */}
          {dashboard.paused && <Alert type="error">⏸ Platform is currently paused — all operations suspended.</Alert>}
          {dashboard.expiryWarnings.length > 0 && <Alert type="warn">⚠ Points expiring soon: {dashboard.expiryWarnings.join(', ')}</Alert>}
          {parseFloat(dashboard.leftover) > 0 && (
            <Alert type="info">
              {parseFloat(dashboard.leftover).toFixed(6)} KRT available to claim from exchanges.{' '}
              <button onClick={() => send('claimLeftoverROY', () => contracts.platform.claimLeftoverROY())}
                style={{ background: 'none', border: 'none', color: C.green, fontWeight: 700, cursor: 'pointer', fontFamily: "'Open Sans', sans-serif", fontSize: 12, textDecoration: 'underline', padding: 0 }}>
                Claim →
              </button>
            </Alert>
          )}

          {/* Stat Cards */}
          <motion.div variants={stagger} className="grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'KRT Balance',     value: parseFloat(dashboard.roy).toFixed(4),       unit: 'KRT', hero: true },
              { label: 'Portfolio Value', value: parseFloat(dashboard.portfolio).toFixed(4), unit: 'KRT' },
              { label: 'Programs',        value: dashboard.count,                            unit: 'active' },
              { label: 'Exchange Fee',    value: dashboard.fee,                              unit: 'bps' },
            ].map((s, i) => (
              <motion.div key={i} variants={fadeUp} style={{
                borderRadius: 14, padding: '16px 14px',
                background: s.hero ? 'linear-gradient(135deg, #85BB65, #5E9444)' : C.surface,
                border: `1px solid ${s.hero ? 'transparent' : C.border}`,
                boxShadow: s.hero ? `0 8px 32px ${C.greenGlow}` : 'none',
              }}>
                <div style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 600, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: s.hero ? 'rgba(255,255,255,0.7)' : C.text3, marginBottom: 8 }}>{s.label}</div>
                <div style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 800, fontSize: 22, color: s.hero ? '#fff' : C.text, lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 11, color: s.hero ? 'rgba(255,255,255,0.6)' : C.text3, marginTop: 4 }}>{s.unit}</div>
              </motion.div>
            ))}
          </motion.div>

          {/* Balances */}
          <Card title="Point Balances" badge={`${dashboard.balances.length} program${dashboard.balances.length !== 1 ? 's' : ''}`}>
            {dashboard.balances.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', fontFamily: "'Open Sans', sans-serif", fontSize: 13, color: C.text3 }}>
                No point balances found
              </div>
            ) : (
              <div style={{ overflowX: 'auto', margin: '0 -18px', padding: '0 18px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 360 }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                      {['Program', 'Points', 'Tier', 'Expires'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontFamily: "'Raleway', sans-serif", fontWeight: 700, fontSize: 10, color: C.text3, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.balances.map((b, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                        <td style={{ padding: '12px 10px' }}>
                          <div style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 700, fontSize: 13, color: C.text }}>{b.name}</div>
                          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: C.text3, marginTop: 2 }}>{b.addr.slice(0, 8)}···</div>
                        </td>
                        <td style={{ padding: '12px 10px', fontFamily: "'Raleway', sans-serif", fontWeight: 800, fontSize: 18, color: C.green }}>{Number(b.points).toLocaleString()}</td>
                        <td style={{ padding: '12px 10px' }}>
                          <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 20, background: b.tier > 0 ? C.greenGlow : C.surface2, color: b.tier > 0 ? C.green : C.text3, border: `1px solid ${b.tier > 0 ? C.greenBorder : C.border}` }}>
                            {['Standard', 'Silver', 'Gold', 'Platinum'][b.tier]}
                          </span>
                        </td>
                        <td style={{ padding: '12px 10px', fontFamily: "'Open Sans', sans-serif", fontSize: 12, color: b.expiry === '0' ? C.text3 : C.amber }}>
                          {b.expiry === '0' ? 'No expiry' : new Date(Number(b.expiry) * 1000).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', border: `2px solid ${C.border}`, borderTopColor: C.green, margin: '0 auto 16px', animation: 'spin 1s linear infinite' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <div style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 13, color: C.text3 }}>Loading your dashboard…</div>
        </div>
      )}
    </motion.div>
  );
}

/* ─── Vendor Tab ─────────────────────────────────────────────────── */
function VendorTab({ contracts, account, send, loading, showToast }) {
  const [apply, setApply]   = useState({ name: '', rate: '', cap: '' });
  const [pts, setPts]       = useState({ user: '', amount: '' });
  const [batchRaw, setBatch]= useState('');
  const [pointCap, setPointCap] = useState('');
  const [rate, setRate]     = useState('');
  const [timelockLeft, setTimelockLeft] = useState(null);
  const [rateReady, setRateReady]       = useState(false);
  const [tierRates, setTierRates] = useState({ silver: '', gold: '', platinum: '' });
  const [tierUser, setTierUser]   = useState({ addr: '', tier: '0' });
  const [promo, setPromo]   = useState({ mult: '2', dur: '604800' });
  const [windDur, setWindDur] = useState('2592000');

  const checkTimelock = async () => {
    try {
      const [ready, remaining] = await Promise.all([
        contracts.platform.isRateChangeReady(account),
        contracts.platform.rateTimelockRemaining(account),
      ]);
      setRateReady(ready); setTimelockLeft(Number(remaining));
    } catch {}
  };

  const parseBatch = () => {
    const lines = batchRaw.trim().split('\n').filter(Boolean);
    const addrs = [], amounts = [];
    for (const l of lines) {
      const [a, n] = l.split(',').map(s => s.trim());
      if (a && n) { addrs.push(a); amounts.push(n); }
    }
    return { addrs, amounts };
  };

  return (
    <motion.div variants={stagger}>
      <PageHeader title="Vendor" subtitle="Manage your loyalty program" />
      <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>

        <Card title="Apply as Vendor" badge="Free" glow>
          <Field label="Program Name" value={apply.name} onChange={v => setApply({...apply, name:v})} placeholder="SkywardsMiles" />
          <Field label="Rate (KRT wei / point)" value={apply.rate} onChange={v => setApply({...apply, rate:v})} placeholder="1000000000000000" mono />
          <Field label="Mint Cap (KRT wei)" value={apply.cap} onChange={v => setApply({...apply, cap:v})} placeholder="1000000000000000000000000" mono />
          <Btn full label="Apply as Vendor" loading={loading} variant="primary"
            onClick={() => send('applyAsVendor', () => contracts.platform.applyAsVendor(apply.name, apply.rate, apply.cap))} />
        </Card>

        <Card title="Award Points">
          <Field label="User Address" value={pts.user} onChange={v => setPts({...pts, user:v})} placeholder="0x..." mono />
          <Field label="Points" value={pts.amount} onChange={v => setPts({...pts, amount:v})} placeholder="1000" type="number" />
          <Btn full label="Award Points" loading={loading} variant="green"
            onClick={() => send('addUserPoints', () => contracts.platform.addUserPoints(pts.user, pts.amount))} />
        </Card>

        <Card title="Batch Award">
          <TextArea value={batchRaw} onChange={setBatch} placeholder={"0xAbc..., 1000\n0xDef..., 500"} label="One entry per line: address, amount" />
          <Btn full label="Batch Award" loading={loading} variant="green" onClick={() => {
            const { addrs, amounts } = parseBatch();
            if (!addrs.length) { showToast('No valid rows', false); return; }
            send('batchAddUserPoints', () => contracts.platform.batchAddUserPoints(addrs, amounts));
          }} />
        </Card>

        <Card title="User Point Cap">
          <Field label="Maximum points per user" value={pointCap} onChange={setPointCap} placeholder="100000" type="number" />
          <Btn full label="Set Cap" loading={loading} variant="primary"
            onClick={() => send('setUserPointCap', () => contracts.platform.setUserPointCap(pointCap))} />
        </Card>

        <Card title="Rate Change">
          <Field label="New Rate (KRT wei / point)" value={rate} onChange={setRate} placeholder="2000000000000000" mono />
          {timelockLeft !== null && (
            <Alert type={rateReady ? 'info' : 'warn'}>
              {rateReady ? '✓ Ready to apply' : `⏳ ${Math.ceil(timelockLeft / 3600)}h remaining on timelock`}
            </Alert>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Btn label="Propose" loading={loading} variant="primary" onClick={() => send('proposeRateChange', () => contracts.platform.proposeRateChange(rate))} />
            <Btn label="Apply"   loading={loading} variant="green"   onClick={() => send('applyRateChange',   () => contracts.platform.applyRateChange())} />
            <Btn label="Cancel"  loading={loading} variant="red"     onClick={() => send('cancelRateChange',  () => contracts.platform.cancelRateChange())} />
            <Btn label="Check"   loading={false}   variant="ghost"   onClick={checkTimelock} />
          </div>
        </Card>

        <Card title="Tier Rates">
          <Field label="Silver (bps)"   value={tierRates.silver}   onChange={v => setTierRates({...tierRates, silver:v})}   placeholder="11000" type="number" />
          <Field label="Gold (bps)"     value={tierRates.gold}     onChange={v => setTierRates({...tierRates, gold:v})}     placeholder="12500" type="number" />
          <Field label="Platinum (bps)" value={tierRates.platinum} onChange={v => setTierRates({...tierRates, platinum:v})} placeholder="15000" type="number" />
          <Btn full label="Set Tier Rates" loading={loading} variant="primary"
            onClick={() => send('setTierRates', () => contracts.platform.setTierRates(tierRates.silver, tierRates.gold, tierRates.platinum))} />
        </Card>

        <Card title="Set User Tier">
          <Field label="User Address" value={tierUser.addr} onChange={v => setTierUser({...tierUser, addr:v})} placeholder="0x..." mono />
          <Select label="Tier" value={tierUser.tier} onChange={v => setTierUser({...tierUser, tier:v})}
            options={['Standard','Silver','Gold','Platinum'].map((t, i) => ({ value: String(i), label: `${i} — ${t}` }))} />
          <Btn full label="Set Tier" loading={loading} variant="primary"
            onClick={() => send('setUserTier', () => contracts.platform.setUserTier(tierUser.addr, tierUser.tier))} />
        </Card>

        <Card title="Promotions">
          <Field label="Multiplier" value={promo.mult} onChange={v => setPromo({...promo, mult:v})} placeholder="2" type="number" />
          <Field label="Duration (seconds)" value={promo.dur} onChange={v => setPromo({...promo, dur:v})} placeholder="604800" type="number" />
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn label="Start" loading={loading} variant="primary" onClick={() => send('startPromotion', () => contracts.platform.startPromotion(promo.mult, promo.dur))} />
            <Btn label="End"   loading={loading} variant="red"     onClick={() => send('endPromotion',   () => contracts.platform.endPromotion())} />
          </div>
        </Card>

        <Card title="Wind Down">
          <Alert type="error">This initiates an orderly closure. Users can still redeem until expiry.</Alert>
          <Field label="Duration (seconds)" value={windDur} onChange={setWindDur} placeholder="2592000 = 30 days" type="number" />
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn label="Start Wind Down"  loading={loading} variant="red"   onClick={() => send('startWindDown',  () => contracts.platform.startWindDown(windDur))} />
            <Btn label="Cancel"           loading={loading} variant="ghost" onClick={() => send('cancelWindDown', () => contracts.platform.cancelWindDown())} />
          </div>
        </Card>

        <Card title="Self Pause / Resume">
          <div style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 12, color: C.text3, marginBottom: 14 }}>Temporarily suspend your program without admin involvement.</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn label="⏸ Pause"  loading={loading} variant="red"   onClick={() => send('selfPause',  () => contracts.platform.selfPause())} />
            <Btn label="▶ Resume" loading={loading} variant="green" onClick={() => send('selfResume', () => contracts.platform.selfResume())} />
          </div>
        </Card>

      </div>
    </motion.div>
  );
}

/* ─── User Tab ───────────────────────────────────────────────────── */
function UserTab({ contracts, account, send, loading, showToast }) {
  const [conv, setConv]       = useState({ vendor: '', points: '', min: '0' });
  const [exch, setExch]       = useState({ from: '', to: '', points: '', min: '0' });
  const [tx, setTx]           = useState({ to: '', vendor: '', points: '' });
  const [burnAmt, setBurnAmt] = useState('1');
  const [checkV, setCheckV]   = useState('');
  const [simConv, setSimConv] = useState('');
  const [simEx, setSimEx]     = useState('');

  const simulate = async () => {
    try {
      const s = await contracts.views.simulateConvert(account, conv.vendor, conv.points);
      setSimConv(`KRT out: ${ethers.formatEther(s[0])} · Rate: ${s[1].toString()}`);
    } catch (e) { setSimConv('Error: ' + e.message.slice(0, 60)); }
  };

  const simulateEx = async () => {
    try {
      const s = await contracts.views.simulateExchange(exch.from, exch.to, exch.points);
      setSimEx(`Points out: ${s[0].toString()} · Fee: ${ethers.formatEther(s[2])} KRT`);
    } catch (e) { setSimEx('Error: ' + e.message.slice(0, 60)); }
  };

  return (
    <motion.div variants={stagger}>
      <PageHeader title="User" subtitle="Exchange, convert, and manage your points" />
      <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>

        <Card title="Convert → KRT" glow>
          <Field label="Vendor Address" value={conv.vendor} onChange={v => setConv({...conv, vendor:v})} placeholder="0x..." mono />
          <Field label="Points" value={conv.points} onChange={v => setConv({...conv, points:v})} placeholder="500" type="number" />
          <Field label="Min KRT Out (slippage)" value={conv.min} onChange={v => setConv({...conv, min:v})} placeholder="0" />
          {simConv && <Alert type="info">{simConv}</Alert>}
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn label="Simulate" loading={false}   variant="ghost"   onClick={simulate} />
            <Btn label="Convert"  loading={loading} variant="primary" onClick={() => send('convertToROY', () => contracts.platform.convertToROY(conv.vendor, conv.points, conv.min))} />
          </div>
        </Card>

        <Card title="Exchange Points">
          <Field label="From Vendor"    value={exch.from}   onChange={v => setExch({...exch, from:v})}   placeholder="0x..." mono />
          <Field label="To Vendor"      value={exch.to}     onChange={v => setExch({...exch, to:v})}     placeholder="0x..." mono />
          <Field label="Points to Spend" value={exch.points} onChange={v => setExch({...exch, points:v})} placeholder="1000" type="number" />
          {simEx && <Alert type="info">{simEx}</Alert>}
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn label="Simulate" loading={false}   variant="ghost"   onClick={simulateEx} />
            <Btn label="Exchange" loading={loading} variant="primary" onClick={() => send('exchangePoints', () => contracts.platform.exchangePoints(exch.from, exch.to, exch.points, exch.min || '0'))} />
          </div>
        </Card>

        <Card title="Transfer Points">
          <Field label="Recipient"      value={tx.to}     onChange={v => setTx({...tx, to:v})}     placeholder="0x..." mono />
          <Field label="Vendor Address" value={tx.vendor} onChange={v => setTx({...tx, vendor:v})} placeholder="0x..." mono />
          <Field label="Points"         value={tx.points} onChange={v => setTx({...tx, points:v})} placeholder="100" type="number" />
          <Btn full label="Transfer" loading={loading} variant="primary"
            onClick={() => send('transferPoints', () => contracts.platform.transferPoints(tx.to, tx.vendor, tx.points))} />
        </Card>

        <Card title="Check Expiry">
          <Field label="Vendor Address" value={checkV} onChange={setCheckV} placeholder="0x..." mono />
          <Btn full label="Check Point Status" loading={loading} variant="ghost"
            onClick={() => send('checkPointStatus', () => contracts.platform.checkPointStatus(checkV))} />
        </Card>

        <Card title="Claim & Burn">
          <Btn full label="Claim Leftover KRT" loading={loading} variant="green"
            onClick={() => send('claimLeftoverROY', () => contracts.platform.claimLeftoverROY())} />
          <Divider label="OR BURN" />
          <Field label="Burn Amount (KRT)" value={burnAmt} onChange={setBurnAmt} placeholder="1" type="number" />
          <Btn full label="🔥 Burn KRT" loading={loading} variant="red"
            onClick={() => send('burn', () => contracts.platform.burn(ethers.parseEther(burnAmt || '1')))} />
        </Card>

      </div>
    </motion.div>
  );
}

/* ─── Links Tab ──────────────────────────────────────────────────── */
function LinksTab({ contracts, account, send, loading, showToast }) {
  const [cfg, setCfg]             = useState({ signer: '', rateBps: '9000', maxPerUser: '' });
  const [linkData, setLinkData]   = useState({ vendor: '', extId: '', maxPts: '', nonce: '', expiry: '', sig: '' });
  const [importData, setImportData] = useState({ vendor: '', amount: '' });
  const [unlinkV, setUnlinkV]     = useState('');
  const [revokeU, setRevokeU]     = useState('');
  const [statusQ, setStatusQ]     = useState({ vendor: '' });
  const [statusResult, setStatusResult] = useState(null);
  const [simResult, setSimResult] = useState('');

  const queryStatus = async () => {
    try {
      const s = await contracts.lm.getLinkStatus(account, statusQ.vendor);
      setStatusResult({ externalId: s[0], imported: s[1].toString(), remaining: s[2].toString(), rateBps: s[3].toString(), linkedAt: Number(s[4]), active: s[5] });
    } catch (e) { showToast('Error: ' + e.message.slice(0, 60), false); }
  };

  const simImport = async () => {
    try {
      const s = await contracts.lm.simulateImport(importData.vendor, importData.amount);
      setSimResult(`Points in: ${s[0].toString()} · Haircut: ${s[1].toString()}`);
    } catch (e) { setSimResult('Error: ' + e.message.slice(0, 60)); }
  };

  return (
    <motion.div variants={stagger}>
      <PageHeader title="Links" subtitle="Import points from external loyalty programs" />
      <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>

        <Card title="Configure Linking" badge="Vendor" glow>
          <Field label="Signer Key Address" value={cfg.signer} onChange={v => setCfg({...cfg, signer:v})} placeholder="0x..." mono />
          <Field label="Import Rate (bps)" value={cfg.rateBps} onChange={v => setCfg({...cfg, rateBps:v})} placeholder="9000" type="number" />
          <Field label="Max Import Per User" value={cfg.maxPerUser} onChange={v => setCfg({...cfg, maxPerUser:v})} placeholder="100000" type="number" />
          <Btn full label="Configure" loading={loading} variant="primary"
            onClick={() => send('configureLinking', () => contracts.lm.configureLinking(cfg.signer, cfg.rateBps, cfg.maxPerUser))} />
        </Card>

        <Card title="Toggle Linking" badge="Vendor">
          <div style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 12, color: C.text3, marginBottom: 14 }}>Enable or disable linking and importing independently.</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Btn label="Enable Both"    loading={loading} variant="green" onClick={() => send('toggleLinking', () => contracts.lm.toggleLinking(true, true))} />
            <Btn label="Disable Import" loading={loading} variant="ghost" onClick={() => send('toggleLinking', () => contracts.lm.toggleLinking(true, false))} />
            <Btn label="Disable All"    loading={loading} variant="red"   onClick={() => send('toggleLinking', () => contracts.lm.toggleLinking(false, false))} />
          </div>
        </Card>

        <Card title="Link External Account" badge="User">
          <Field label="Vendor Address"   value={linkData.vendor} onChange={v => setLinkData({...linkData, vendor:v})} placeholder="0x..." mono />
          <Field label="External Account" value={linkData.extId}  onChange={v => setLinkData({...linkData, extId:v})}  placeholder="BOOTS-12345" />
          <Field label="Max Points"       value={linkData.maxPts} onChange={v => setLinkData({...linkData, maxPts:v})} placeholder="50000" type="number" />
          <Field label="Nonce"            value={linkData.nonce}  onChange={v => setLinkData({...linkData, nonce:v})}  placeholder="1" type="number" />
          <Field label="Expiry (unix)"    value={linkData.expiry} onChange={v => setLinkData({...linkData, expiry:v})} placeholder="1800000000" type="number" />
          <Field label="Signature"        value={linkData.sig}    onChange={v => setLinkData({...linkData, sig:v})}    placeholder="0x..." mono />
          <Btn full label="Link Account" loading={loading} variant="primary"
            onClick={() => send('linkExternalAccount', () => contracts.lm.linkExternalAccount(linkData.vendor, linkData.extId, linkData.maxPts, linkData.nonce, linkData.expiry, linkData.sig))} />
        </Card>

        <Card title="Import Points" badge="User">
          <Field label="Vendor Address"   value={importData.vendor} onChange={v => setImportData({...importData, vendor:v})} placeholder="0x..." mono />
          <Field label="Amount to Import" value={importData.amount} onChange={v => setImportData({...importData, amount:v})} placeholder="5000" type="number" />
          {simResult && <Alert type="info">{simResult}</Alert>}
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn label="Simulate" loading={false}   variant="ghost"   onClick={simImport} />
            <Btn label="Import"   loading={loading} variant="primary" onClick={() => send('importPoints', () => contracts.lm.importPoints(importData.vendor, importData.amount))} />
          </div>
        </Card>

        <Card title="Link Status">
          <Field label="Vendor Address" value={statusQ.vendor} onChange={v => setStatusQ({vendor:v})} placeholder="0x..." mono />
          <Btn full label="Check Status" loading={false} variant="ghost" onClick={queryStatus} />
          {statusResult && (
            <div style={{ marginTop: 14 }}>
              <KV label="External ID" value={statusResult.externalId || '—'} mono />
              <KV label="Active"      value={statusResult.active ? 'Yes' : 'No'} highlight={statusResult.active} />
              <KV label="Imported"    value={Number(statusResult.imported).toLocaleString() + ' pts'} />
              <KV label="Remaining"   value={Number(statusResult.remaining).toLocaleString() + ' pts'} highlight />
              <KV label="Rate"        value={statusResult.rateBps + ' bps'} />
              <KV label="Linked at"   value={statusResult.linkedAt ? new Date(statusResult.linkedAt * 1000).toLocaleString() : '—'} />
            </div>
          )}
        </Card>

        <Card title="Unlink / Revoke">
          <Field label="Vendor to unlink from" value={unlinkV} onChange={setUnlinkV} placeholder="0x..." mono />
          <Btn full label="Unlink My Account" loading={loading} variant="red"
            onClick={() => send('unlinkAccount', () => contracts.lm.unlinkAccount(unlinkV))} />
          <Divider label="VENDOR ONLY" />
          <Field label="User address to revoke" value={revokeU} onChange={setRevokeU} placeholder="0x..." mono />
          <Btn full label="Revoke Link" loading={loading} variant="red"
            onClick={() => send('revokeLink', () => contracts.lm.revokeLink(revokeU))} />
        </Card>

      </div>
    </motion.div>
  );
}

/* ─── Admin Tab ──────────────────────────────────────────────────── */
function AdminTab({ contracts, account, send, loading, showToast }) {
  const [vendor, setVendor]       = useState('');
  const [mintCap, setMintCap]     = useState('');
  const [ptCapV, setPtCapV]       = useState('');
  const [ptCapAmt, setPtCapAmt]   = useState('');
  const [rescue, setRescue]       = useState({ user: '', from: '', to: '' });
  const [fee, setFee]             = useState('');
  const [treasury, setTreasury]   = useState('');
  const [lmAddr, setLmAddr]       = useState('');
  const [analytics, setAnalytics] = useState(null);

  const loadAnalytics = async () => {
    try {
      const provider = contracts.provider;
      const pAddr = await contracts.platform.getAddress();
      const iface = new ethers.Interface(["function getPlatformAnalytics() external view returns (uint256, uint256, uint256, uint256, uint256)"]);
      const result = await provider.call({ to: pAddr, data: iface.encodeFunctionData('getPlatformAnalytics') });
      const [totalVendors, totalSupply, totalFees, activeVendors, totalUsers] = iface.decodeFunctionResult('getPlatformAnalytics', result);
      setAnalytics({ totalVendors: totalVendors.toString(), totalSupply: ethers.formatEther(totalSupply), totalFees: ethers.formatEther(totalFees), activeVendors: activeVendors.toString(), totalUsers: totalUsers.toString() });
    } catch (e) { showToast('Analytics error: ' + e.message.slice(0, 60), false); }
  };

  return (
    <motion.div variants={stagger}>
      <PageHeader title="Admin" subtitle="Platform management — restricted to admin role" />
      <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>

        <Card title="Platform Analytics" glow>
          <Btn full label="Load Analytics" loading={false} variant="ghost" onClick={loadAnalytics} />
          {analytics && (
            <div style={{ marginTop: 14 }}>
              <KV label="Total Vendors"  value={analytics.totalVendors}  highlight />
              <KV label="Active Vendors" value={analytics.activeVendors} highlight />
              <KV label="Total Supply"   value={parseFloat(analytics.totalSupply).toFixed(2) + ' KRT'} highlight />
              <KV label="Fees Collected" value={parseFloat(analytics.totalFees).toFixed(4) + ' KRT'} />
              <KV label="Total Users"    value={analytics.totalUsers} />
            </div>
          )}
        </Card>

        <Card title="Vendor Management">
          <Field label="Vendor Address" value={vendor} onChange={setVendor} placeholder="0x..." mono />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <Btn label="✓ Approve"  loading={loading} variant="green" onClick={() => send('approveVendor',    () => contracts.platform.approveVendor(vendor))} />
            <Btn label="✗ Reject"   loading={loading} variant="red"   onClick={() => send('rejectVendor',     () => contracts.platform.rejectVendor(vendor))} />
            <Btn label="Deactivate" loading={loading} variant="red"   onClick={() => send('deactivateVendor', () => contracts.platform.deactivateVendor(vendor))} />
            <Btn label="Reactivate" loading={loading} variant="green" onClick={() => send('reactivateVendor', () => contracts.platform.reactivateVendor(vendor))} />
          </div>
          <Field label="Override Mint Cap (KRT wei)" value={mintCap} onChange={setMintCap} placeholder="1000000000000000000000000" mono />
          <Btn full label="Set Mint Cap" loading={loading} variant="primary"
            onClick={() => send('setVendorMintCap', () => contracts.platform.setVendorMintCap(vendor, mintCap))} />
        </Card>

        <Card title="User Point Cap Override">
          <Field label="Vendor Address"   value={ptCapV}   onChange={setPtCapV}   placeholder="0x..." mono />
          <Field label="New Cap (points)" value={ptCapAmt} onChange={setPtCapAmt} placeholder="100000" type="number" />
          <Btn full label="Set Cap" loading={loading} variant="primary"
            onClick={() => send('adminSetUserPointCap', () => contracts.platform.adminSetUserPointCap(ptCapV, ptCapAmt))} />
        </Card>

        <Card title="Rescue Stranded Points">
          <Alert type="warn">Only use for wind-down scenarios where a user's points are inaccessible.</Alert>
          <Field label="User Address" value={rescue.user} onChange={v => setRescue({...rescue, user:v})} placeholder="0x..." mono />
          <Field label="From Vendor"  value={rescue.from} onChange={v => setRescue({...rescue, from:v})} placeholder="0x..." mono />
          <Field label="To Vendor"    value={rescue.to}   onChange={v => setRescue({...rescue, to:v})}   placeholder="0x..." mono />
          <Btn full label="Rescue Points" loading={loading} variant="amber"
            onClick={() => send('rescueStranded', () => contracts.platform.rescueStranded(rescue.user, rescue.from, rescue.to))} />
        </Card>

        <Card title="Platform Settings">
          <Field label="Exchange Fee (bps, max 500)" value={fee} onChange={setFee} placeholder="100" type="number" />
          <Btn full label="Set Fee" loading={loading} variant="primary" onClick={() => send('setExchangeFee', () => contracts.platform.setExchangeFee(fee))} />
          <Divider />
          <Field label="Treasury Address" value={treasury} onChange={setTreasury} placeholder="0x..." mono />
          <Btn full label="Set Treasury" loading={loading} variant="primary" onClick={() => send('setTreasury', () => contracts.platform.setTreasury(treasury))} />
          <Divider />
          <Field label="LinkManager Address" value={lmAddr} onChange={setLmAddr} placeholder="0x..." mono />
          <Btn full label="Set LinkManager" loading={loading} variant="primary" onClick={() => send('setLinkManager', () => contracts.platform.setLinkManager(lmAddr))} />
        </Card>

        <Card title="Emergency Controls">
          <Alert type="error">Pausing halts all exchanges, conversions, and transfers platform-wide.</Alert>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn label="⏸ Pause Platform" loading={loading} variant="red"   onClick={() => send('pausePlatform',   () => contracts.platform.pausePlatform())} />
            <Btn label="▶ Unpause"        loading={loading} variant="green" onClick={() => send('unpausePlatform', () => contracts.platform.unpausePlatform())} />
          </div>
        </Card>

      </div>
    </motion.div>
  );
}
